import Confetti from '../Confetti'
import { IconCrown, IconReplay } from '../icons'

/**
 * PartyTrilogyFinal
 *
 * Final standings screen shown when party.status === 'final_standings'.
 * Used by both the host (isHost=true, shows action buttons)
 * and player phones (isHost=false, shows "Waiting for host...").
 *
 * Layout:
 *   - Confetti burst + champion declaration (shimmer + crown)
 *   - Podium (top 3) with gold/silver/bronze tiers
 *   - Full ranked leaderboard
 *   - Collapsible round-by-round breakdown
 *   - Host only: "Play Again" / "End Party"
 *
 * Props
 *   party       — live Firestore party doc
 *   isHost      — true: show Play Again / End Party buttons
 *   onPlayAgain — host: reset and return to party mode select
 *   onDone      — host: end the party entirely
 */

function fmtMs(ms) {
  const s   = Math.round((ms ?? 0) / 1000)
  const m   = String(Math.floor(s / 60)).padStart(2, '0')
  const sec = String(s % 60).padStart(2, '0')
  return `${m}:${sec}`
}

/**
 * Numbered rank chip — gold/silver/bronze gradient for top 3, plain "#N"
 * muted for everything else. Tier colors are intentionally theme-stable
 * (universal podium colors, like the difficulty pills).
 */
function RankChip({ place }) {
  if (place === 1) return <span className="trilogy-rank-chip trilogy-rank-chip--gold">1</span>
  if (place === 2) return <span className="trilogy-rank-chip trilogy-rank-chip--silver">2</span>
  if (place === 3) return <span className="trilogy-rank-chip trilogy-rank-chip--bronze">3</span>
  return <span className="trilogy-rank-chip trilogy-rank-chip--plain">#{place}</span>
}

// ── Podium slot component ─────────────────────────────────────────────────────
function PodiumSlot({ player, totalHops, place }) {
  const placeClass = place === 1 ? 'first' : place === 2 ? 'second' : 'third'
  const initial = (player.username || 'G').charAt(0).toUpperCase()
  return (
    <div className={`trilogy-final__podium-slot trilogy-final__podium-slot--${placeClass}`}>
      <span className="trilogy-final__podium-rank">
        <RankChip place={place} />
      </span>
      <div className="trilogy-final__podium-avatar">
        {player.avatar
          ? <img src={player.avatar} alt={player.username} />
          : <span className="party-avatar__initial">{initial}</span>}
      </div>
      <p className="trilogy-final__podium-name">{player.username || 'Guest'}</p>
      {totalHops != null && (
        <p className="trilogy-final__podium-stat">{totalHops} hops</p>
      )}
      <div className={`trilogy-final__podium-block trilogy-final__podium-block--${place}`} />
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PartyTrilogyFinal({ party, isHost, onPlayAgain, onDone, onBackToLobby }) {
  const players    = party?.players    ?? {}
  const cumulative = party?.cumulativeScores ?? {}
  const rounds     = party?.rounds     ?? {}

  // Sort by total hops asc, total time asc
  const sorted = Object.entries(players).sort(([aUid], [bUid]) => {
    const a = cumulative[aUid] ?? { totalHops: 999, totalTimeMs: 99_999_999 }
    const b = cumulative[bUid] ?? { totalHops: 999, totalTimeMs: 99_999_999 }
    if (a.totalHops !== b.totalHops) return a.totalHops - b.totalHops
    return a.totalTimeMs - b.totalTimeMs
  })

  const [goldEntry, silverEntry, bronzeEntry] = sorted
  const champion = goldEntry?.[1]?.username ?? 'Guest'

  // Round-by-round breakdown: per-round results for the detail section
  const roundNums = [1, 2, 3].filter((rn) =>
    rounds[rn]?.results && Object.keys(rounds[rn].results).length > 0
  )

  return (
    <div className="trilogy-final">
      <Confetti count={60} />

      {/* ── Champion declaration ─────────────────────────────────────────── */}
      <p className="trilogy-final__eyebrow">THE TRILOGY · FINAL RESULTS</p>
      <div className="trilogy-final__crown" aria-hidden="true">
        <IconCrown size={28} />
      </div>
      <h2 className="trilogy-final__champion">{champion} wins the Trilogy!</h2>

      {/* ── Podium ────────────────────────────────────────────────────────── */}
      {sorted.length > 0 && (
        <div className="trilogy-final__podium">
          {/* Silver — left */}
          {silverEntry && (
            <PodiumSlot
              player={silverEntry[1]}
              totalHops={cumulative[silverEntry[0]]?.totalHops}
              place={2}
            />
          )}
          {/* Gold — center / tallest */}
          {goldEntry && (
            <PodiumSlot
              player={goldEntry[1]}
              totalHops={cumulative[goldEntry[0]]?.totalHops}
              place={1}
            />
          )}
          {/* Bronze — right */}
          {bronzeEntry && (
            <PodiumSlot
              player={bronzeEntry[1]}
              totalHops={cumulative[bronzeEntry[0]]?.totalHops}
              place={3}
            />
          )}
        </div>
      )}

      {/* ── Full leaderboard ─────────────────────────────────────────────── */}
      <div className="trilogy-final__table">
        <p className="trilogy-final__table-header">Full Standings</p>

        {sorted.map(([uid, p], i) => {
          const cum   = cumulative[uid]
          const roundsCompleted = [1, 2, 3].filter(
            (rn) => rounds[rn]?.results?.[uid]?.completed
          ).length
          const initial = (p.username || 'G').charAt(0).toUpperCase()

          return (
            <div
              key={uid}
              className={`trilogy-final__row${i < 3 ? ' trilogy-final__row--top' : ''}`}
            >
              {/* Rank chip */}
              <span className="trilogy-final__rank">
                <RankChip place={i + 1} />
              </span>

              {/* Avatar */}
              <div className="trilogy-final__row-avatar">
                {p.avatar
                  ? <img src={p.avatar} alt={p.username} />
                  : <span className="party-avatar__initial">{initial}</span>}
              </div>

              {/* Name + stats */}
              <div className="trilogy-final__row-info">
                <div className="trilogy-final__row-name">{p.username || 'Guest'}</div>
                {cum ? (
                  <div className="trilogy-final__row-stats">
                    {cum.totalHops} hops · {fmtMs(cum.totalTimeMs)}
                  </div>
                ) : (
                  <div className="trilogy-final__row-stats">No data</div>
                )}
              </div>

              {/* Rounds completed badge */}
              <span className="trilogy-final__row-rounds">{roundsCompleted}/3</span>
            </div>
          )
        })}
      </div>

      {/* ── Round-by-round breakdown ────────────────────────────────────────── */}
      {roundNums.length > 0 && (
        <details className="trilogy-final__breakdown">
          <summary className="trilogy-final__breakdown-toggle">
            Round-by-round breakdown
          </summary>

          {roundNums.map((rn) => {
            const rResults = rounds[rn].results
            const rStart   = rounds[rn]?.startActor
            const rGoal    = rounds[rn]?.goalActor
            const rSorted  = Object.entries(rResults).sort((a, b) => {
              if (a[1].hops !== b[1].hops) return a[1].hops - b[1].hops
              return a[1].timeMs - b[1].timeMs
            })
            return (
              <div key={rn} className="trilogy-final__breakdown-round">
                <p className="trilogy-final__breakdown-round-label">Round {rn}</p>
                {rStart && rGoal && (
                  <p className="trilogy-final__breakdown-round-puzzle">
                    {rStart.name} → {rGoal.name}
                  </p>
                )}
                {rSorted.map(([uid, res], idx) => (
                  <div key={uid} className="trilogy-final__breakdown-row">
                    <span className="trilogy-final__breakdown-row__rank">#{idx + 1}</span>
                    <span className="trilogy-final__breakdown-row__name">
                      {players[uid]?.username || 'Guest'}
                    </span>
                    <span className="trilogy-final__breakdown-row__result">
                      {res.completed
                        ? `${res.hops} hops · ${fmtMs(res.timeMs)}`
                        : 'DNF'}
                    </span>
                  </div>
                ))}
              </div>
            )
          })}
        </details>
      )}

      {/* ── Actions ─────────────────────────────────────────────────────────── */}
      {isHost ? (
        <div className="trilogy-final__actions">
          <button className="pb-action-btn pb-action-btn--primary" onClick={onPlayAgain}>
            <IconReplay size={18} aria-hidden="true" />
            <span>Play Again</span>
          </button>
          <button className="pb-action-btn pb-action-btn--muted" onClick={onDone}>
            End Party
          </button>
        </div>
      ) : (
        <>
          <p className="trilogy-final__waiting">Waiting for host...</p>
          <button className="pb-action-btn pb-action-btn--outline" onClick={onBackToLobby} style={{ marginTop: 16 }}>
            Back to Lobby
          </button>
        </>
      )}
    </div>
  )
}
